import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getClientProfile, updateClientProfile } from '../services/api';

const ClientProfile: React.FC<{ clientId?: string }> = ({ clientId = "1" }) => {
  const { t } = useTranslation();
  const [clientData, setClientData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const isMountedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    let cancelled = false;
    const fetchClientData = async () => {
      try {
        const data = await getClientProfile(clientId);
        if (cancelled) return;
        setClientData(data);
      } catch (err) {
        if (cancelled) return;
        setError(t('clientProfile.loadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchClientData();
    return () => {
      cancelled = true;
      isMountedRef.current = false;
    };
  }, [clientId, t]);

  const handleUpdate = async () => {
    try {
      const { name, email, ...restClientData } = clientData || {};
      const payload = {
        ...restClientData,
        client_name: clientData?.client_name ?? clientData?.name ?? '',
        contact_email: clientData?.contact_email ?? clientData?.email ?? ''
      };

      await updateClientProfile(clientId, payload);
      if (!isMountedRef.current) return;
      setIsEditing(false);
      alert(t('clientProfile.profileUpdated'));
    } catch (err) {
      if (!isMountedRef.current) return;
      setError(t('clientProfile.updateFailed'));
    }
  };

  if (loading) return (
    <div className="card">
      <div className="animate-pulse">
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
      </div>
    </div>
  );
  
  if (error) return (
    <div className="card border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900">
      <div className="text-red-700 dark:text-red-200">{error}</div>
    </div>
  );

  return (
    <div className="card">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{t('clientProfile.title')}</h2>
        <button
          onClick={() => setIsEditing(!isEditing)}
          className="btn-secondary text-sm"
        >
          {isEditing ? t('common.cancel') : t('common.edit')}
        </button>
      </div>
      
      {isEditing ? (
        <form onSubmit={(e) => { e.preventDefault(); handleUpdate(); }} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('clientProfile.companyName')}
            </label>
            <input
              type="text"
              value={clientData?.client_name || clientData?.name || ''}
              onChange={(e) => setClientData({ ...clientData, client_name: e.target.value })}
              className="input-field"
              placeholder={t('clientProfile.enterCompanyName')}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('clientProfile.contactEmail')}
            </label>
            <input
              type="email"
              value={clientData?.contact_email || clientData?.email || ''}
              onChange={(e) => setClientData({ ...clientData, contact_email: e.target.value })}
              className="input-field"
              placeholder={t('clientProfile.enterContactEmail')}
            />
          </div>
          <div className="flex space-x-3">
            <button type="submit" className="btn-primary">
              {t('clientProfile.updateProfile')}
            </button>
          </div>
        </form>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">{t('clientProfile.companyName')}</label>
            <p className="text-lg text-gray-900 dark:text-white">{clientData?.client_name || clientData?.name || t('clientProfile.notSpecified')}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">{t('clientProfile.contactEmail')}</label>
            <p className="text-lg text-gray-900 dark:text-white">{clientData?.contact_email || clientData?.email || t('clientProfile.notSpecified')}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">{t('clientProfile.accountStatus')}</label>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
              {t('clientProfile.active')}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientProfile;